// PromptGenerator/components/PromptSidebar.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faBars, faTimes, faLightbulb, faEdit, faRefresh, 
  faArrowLeft, faTrash, faLayerGroup, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import styles from '../PromptLayout.module.css';
import { CompactFilterHeader } from './CompactFilterHeader';
import { PromptEmptyState } from './PromptEmptyState';
import { PromptData } from '../hooks/useProjectData';
import { usePromptFilters } from '../hooks/usePromptFilters';
import { usePromptEditor } from '../hooks/usePromptEditor';

interface PromptSidebarProps {
  project: any;
  prompts: PromptData[];
  loading: boolean;
  setProject: (p: any) => void;
  setPrompts: (p: PromptData[]) => void;
  loadPrompts: () => Promise<void>;
  
  // Actions/State from usePromptActions
  selectedPromptId: string | null;
  deletingPromptId: string | null;
  handleNewPrompt: () => void;
  selectPrompt: (promptId: string) => Promise<void>;
  deletePrompt: (promptId: string, promptTitle: string) => Promise<void>;
  handleNavigateToProject: () => void;
  deletingAll: boolean;  setShowDeleteAllConfirm: (show: boolean) => void;
  setIsBatchModalOpen: (isOpen: boolean) => void;
}

const PromptSidebar: React.FC<PromptSidebarProps> = ({
  project, prompts, loading, setProject, setPrompts, loadPrompts, selectedPromptId, 
  deletingPromptId, handleNewPrompt, selectPrompt, deletePrompt, handleNavigateToProject, 
  deletingAll, setShowDeleteAllConfirm, setIsBatchModalOpen
}) => {
  const { t } = useTranslation('promptGenerator');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Filter/Sort Hook
  const { filteredPrompts, filterStats, filterOptions, setContentTypeFilter, setStatusFilter, setSortOrder, resetFilters } = usePromptFilters(prompts);
  
  // Editor Hook (Only exposing project/prompt title editing logic for the sidebar UI)
  const { 
    editingTitle, editedTitle, setEditedTitle, handleProjectTitleEdit, handleProjectTitleSave, 
    handleProjectTitleKeyPress, editingPromptTitle, editedPromptTitle, setEditedPromptTitle, 
    handlePromptTitleEdit, handlePromptTitleSave, handlePromptTitleKeyPress, setDeletingPromptId
  } = usePromptEditor(project, setProject, null, setPrompts, prompts);

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const handleDeleteAllPrompts = () => { if (prompts.length > 0) setShowDeleteAllConfirm(true); };

  // Wrapper function for single delete that uses the component's state setter
  const deletePromptWrapper = (promptId: string, promptTitle: string) => {
    setDeletingPromptId(promptId);
    deletePrompt(promptId, promptTitle);
  };
  
  // Wrapper for prompt title save to reset editing state locally
  const handlePromptTitleSaveWrapper = (promptId: string) => {
    handlePromptTitleSave(promptId);
  };

  return (
    <div className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
      <div className={styles.sidebarHeader}>
        <button className={styles.toggleButton} onClick={toggleSidebar}>
          <FontAwesomeIcon icon={sidebarCollapsed ? faBars : faTimes} />
        </button>
        {!sidebarCollapsed && (
          <div className={styles.headerContent}>
            <button className={styles.backButton} onClick={handleNavigateToProject}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className={styles.appTitleSection}>
              <h1 className={styles.appTitle}>{t('promptGenerator.sidebar.title')}</h1>
              {project && (
                <div className={styles.projectTitleSection}>
                  {editingTitle ? (
                    <input
                      className={styles.titleInput}
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={handleProjectTitleKeyPress}
                      onBlur={handleProjectTitleSave}
                      autoFocus
                    />
                  ) : (
                    <div className={styles.projectTitleWithEdit}>
                      <h2 className={styles.projectTitle}>{project.title}</h2>
                      <button className={styles.editTitleBtn} onClick={handleProjectTitleEdit} title={t('promptGenerator.sidebar.editTitle')}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </div>
                  )}
                </div>
              )}
              {project?.description && (<p className={styles.projectDescription}>{project.description}</p>)}
            </div>
          </div>
        )}
      </div>

      {!sidebarCollapsed && (
        <>
          <div className={styles.actionButtons}>
            <Button
              className={styles.newButton}
              onClick={() => { handleNewPrompt(); if (window.innerWidth <= 768) toggleSidebar(); }}
              leftSection={<FontAwesomeIcon icon={faPlus} />} fullWidth
            >
              {t('promptGenerator.sidebar.newPrompt')}
            </Button>
            <Button
              className={styles.batchButton}
              onClick={() => { setIsBatchModalOpen(true); if (window.innerWidth <= 768) toggleSidebar(); }}
              leftSection={<FontAwesomeIcon icon={faLayerGroup} />} fullWidth
            >
              {t('promptGenerator.sidebar.batchProcess')}
            </Button>
            <button
              className={styles.refreshButton}
              onClick={loadPrompts}
              title={t('promptGenerator.sidebar.refresh')}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faRefresh} className={loading ? styles.spinning : ''} />
            </button>
          </div>

          <div className={styles.promptsHeader}>
            <h3>{t('promptGenerator.sidebar.prompts')} ({prompts.length})</h3>
            {prompts.length > 0 && (
              <button
                className={styles.deleteAllButton}
                onClick={handleDeleteAllPrompts}
                title={t('promptGenerator.sidebar.deleteAll')}
                disabled={deletingAll}
              >
                {deletingAll ? (<div className={styles.spinner} />) : (<FontAwesomeIcon icon={faTrash} />)}
              </button>
            )}
          </div>

          <CompactFilterHeader
            totalCount={prompts.length}
            filterOptions={filterOptions}
            filterStats={filterStats}
            onContentTypeChange={setContentTypeFilter}
            onStatusChange={setStatusFilter}
            onSortChange={setSortOrder}
            onReset={resetFilters}
          />

          <div className={styles.projectsList}>
            {loading ? (
              <PromptEmptyState type="loading" />
            ) : filteredPrompts.length === 0 ? (
              <PromptEmptyState 
                type={prompts.length === 0 ? "no-prompts" : "no-results"}
                filterActive={filterOptions.contentType !== 'all' || filterOptions.status !== 'all'}
                onCreateNew={handleNewPrompt}
                onResetFilters={resetFilters}
              />
            ) : (
              filteredPrompts.map(prompt => (
                <div key={prompt.id} className={styles.projectItem}>
                  <button
                    className={`${styles.projectHeader} ${selectedPromptId === prompt.id ? styles.selected : ''}`}
                    onClick={() => { selectPrompt(prompt.id); if (window.innerWidth <= 768) toggleSidebar(); }}
                  >
                    <FontAwesomeIcon 
                      icon={faLightbulb} 
                      className={styles.projectIcon}
                      style={{ color: (!prompt.generatedPrompt || prompt.generatedPrompt.trim() === '') ? '#6b7280' : '#3b82f6' }}
                    />
                    <div className={styles.projectInfo}>
                      {editingPromptTitle === prompt.id ? (
                        <input
                          className={styles.promptTitleInput}
                          value={editedPromptTitle}
                          onChange={(e) => setEditedPromptTitle(e.target.value)}
                          onKeyDown={(e) => handlePromptTitleKeyPress(e, prompt.id)}
                          onBlur={() => handlePromptTitleSaveWrapper(prompt.id)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div className={styles.promptTitleContainer}>
                          <span className={styles.projectName}>
                            {prompt.title || t('promptGenerator.sidebar.untitledPrompt')}
                            {(!prompt.generatedPrompt || prompt.generatedPrompt.trim() === '') && (<span className={styles.draftBadge}>{t('promptGenerator.sidebar.draftBadge')}</span>)}
                          </span>
                          <div className={styles.promptActions}>
                            <div className={styles.editPromptTitleBtn} onClick={(e) => { e.stopPropagation(); handlePromptTitleEdit(prompt.id, prompt.title || t('promptGenerator.sidebar.untitledPrompt')); }} title={t('promptGenerator.sidebar.editPromptTitle')}>
                              <FontAwesomeIcon icon={faEdit} />
                            </div>
                            <div
                              className={`${styles.deletePromptBtn} ${deletingPromptId === prompt.id ? styles.deleting : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (deletingPromptId !== prompt.id) deletePromptWrapper(prompt.id, prompt.title || t('promptGenerator.sidebar.untitledPrompt'));
                              }}
                              title={t('promptGenerator.sidebar.deletePrompt')}
                            >
                              <FontAwesomeIcon 
                                icon={deletingPromptId === prompt.id ? faSpinner : faTrash} 
                                className={deletingPromptId === prompt.id ? styles.spinning : ''}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      <small className={styles.projectDate}>{new Date(prompt.createdAt).toLocaleDateString()}</small>
                    </div>
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PromptSidebar;
